import { FormEvent, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import { useAuth } from '../../context/AuthContext';

const siteUrl = 'https://neurasites.ai';

type AuthView = 'login' | 'register' | 'forgot';

const copyByView: Record<AuthView, {
  eyebrow: string;
  title: string;
  description: string;
  submitLabel: string;
}> = {
  login: {
    eyebrow: 'Client portal',
    title: 'Sign in to your dashboard',
    description: 'Access your NeuraSites AI project dashboard with your secure client account.',
    submitLabel: 'Sign in'
  },
  register: {
    eyebrow: 'Create access',
    title: 'Create your client portal account',
    description: 'Register with the email connected to your NeuraSites AI project. We will verify account access before project data is attached.',
    submitLabel: 'Create account'
  },
  forgot: {
    eyebrow: 'Account recovery',
    title: 'Reset your password',
    description: 'Enter your email and Supabase will send secure password reset instructions.',
    submitLabel: 'Send reset link'
  }
};

export default function DashboardPage() {
  const location = useLocation();
  const auth = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const view = useMemo<AuthView>(() => {
    if (location.pathname.endsWith('/register')) return 'register';
    if (location.pathname.endsWith('/forgot-password')) return 'forgot';
    return 'login';
  }, [location.pathname]);

  const copy = copyByView[view];

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!auth.isConfigured) {
      setError('Supabase is not fully configured yet. Add VITE_SUPABASE_ANON_KEY before enabling live dashboard auth.');
      return;
    }

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') || '').trim();
    const password = String(form.get('password') || '');
    const name = String(form.get('name') || '').trim();

    try {
      setSubmitting(true);
      if (view === 'login') {
        await auth.signIn(email, password);
        setMessage('Signed in. Your dashboard is ready.');
      } else if (view === 'register') {
        await auth.signUp(email, password, name);
        setMessage('Account created. Please check your email to confirm access.');
      } else {
        await auth.resetPassword(email);
        setMessage('Password reset email sent. Please check your inbox.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const signedIn = Boolean(auth.user);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950">
      <SEOHead
        title="Client Portal Login | NeuraSites AI"
        description="Secure NeuraSites AI client portal access."
        canonicalUrl={`${siteUrl}/dashboard`}
        robots="noindex, nofollow"
      />
      <Header />

      <main className="pt-20">
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600 dark:text-blue-300">
                {signedIn ? 'Client dashboard' : copy.eyebrow}
              </p>
              <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-950 dark:text-white sm:text-5xl">
                {signedIn ? 'Welcome to your NeuraSites portal' : copy.title}
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                {signedIn
                  ? 'Authentication is active. Project data will appear here after client records and row-level security tables are connected.'
                  : copy.description}
              </p>

              <div className="mt-8 rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-100">
                <strong className="block font-semibold">Supabase-backed auth</strong>
                Login, registration, password reset, and session tracking are wired through Supabase Auth. Client project records should be added only after the Supabase schema/RLS setup is applied.
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 sm:p-8">
              {auth.loading ? (
                <p className="text-center text-gray-600 dark:text-gray-300">Checking session…</p>
              ) : signedIn ? (
                <div className="space-y-5">
                  <div className="rounded-2xl bg-emerald-50 p-5 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
                    <p className="text-sm font-semibold">Signed in as</p>
                    <p className="mt-1 break-all text-lg font-bold">{auth.user?.email}</p>
                  </div>
                  <div className="rounded-2xl border border-dashed border-gray-300 p-5 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
                    Project cards, support tickets, and asset downloads will render here once client records are connected in Supabase.
                  </div>
                  <button
                    type="button"
                    onClick={() => auth.signOut()}
                    className="w-full rounded-xl border border-gray-300 px-5 py-3 font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6 flex rounded-full bg-gray-100 p-1 text-sm font-medium dark:bg-gray-800">
                    <Link className={`flex-1 rounded-full px-4 py-2 text-center ${view === 'login' ? 'bg-white text-blue-700 shadow dark:bg-gray-950 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300'}`} to="/dashboard">Login</Link>
                    <Link className={`flex-1 rounded-full px-4 py-2 text-center ${view === 'register' ? 'bg-white text-blue-700 shadow dark:bg-gray-950 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300'}`} to="/dashboard/register">Register</Link>
                    <Link className={`flex-1 rounded-full px-4 py-2 text-center ${view === 'forgot' ? 'bg-white text-blue-700 shadow dark:bg-gray-950 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300'}`} to="/dashboard/forgot-password">Forgot</Link>
                  </div>

                  <form onSubmit={onSubmit} className="space-y-4">
                    {view === 'register' && (
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="name">Name</label>
                        <input id="name" name="name" type="text" autoComplete="name" className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950" />
                      </div>
                    )}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="email">Email</label>
                      <input id="email" name="email" type="email" autoComplete="email" required className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950" />
                    </div>

                    {view !== 'forgot' && (
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="password">Password</label>
                        <input id="password" name="password" type="password" autoComplete={view === 'register' ? 'new-password' : 'current-password'} minLength={8} required className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950" />
                      </div>
                    )}

                    <button type="submit" disabled={submitting} className="w-full rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
                      {submitting ? 'Working…' : copy.submitLabel}
                    </button>
                  </form>

                  {!auth.isConfigured && (
                    <p className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                      Supabase project URL is set, but the public anon key is still needed in <code>VITE_SUPABASE_ANON_KEY</code> before live auth works.
                    </p>
                  )}
                  {message && <p className="mt-4 rounded-xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950/40 dark:text-blue-100">{message}</p>}
                  {error && <p className="mt-4 rounded-xl bg-red-50 p-4 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-100">{error}</p>}
                </>
              )}

              <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Need access now? <a className="font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-300" href="mailto:hello@neurasites.ai">Email NeuraSites AI</a>.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
